<template>
  <div>
    <div v-if="loading.client && !client" class="h-100 d-flex justify-center align-center">
      <v-progress-circular :size="50" color="primary" indeterminate/>
    </div>

    <BackBtn />
    <BreadCrumbs :items="breadcrumbs" />

    <div v-if="client && isCorpSelf" class="mb-4">
      <h1 class="font-lg">Личный кабинет</h1>
      <div class="grey--text text--darken-1">{{ client.company || contactName }}</div>
      <CorpActivityChip v-if="currentTab === 'overview'" :source="client" :small="false" class="mt-2" />
    </div>

    <ProfileTopPanel v-if="client && !isCorpSelf"
      userTypeName="Корпоративный клиент"
      :name="client.company || contactName || 'Компания не указана'"
      :city="client.city"
      :phone="client.phone"
      :email="client.email"
      permssionEdit="edit_corpclient"
      permssionDelete="delete_corpclient"
      whoDelete="корпоративного клиента"
      @delete="onDelete()" @edit="onEdit()"
    >
      <div class="profile-header-item" style="margin-top:0">
        <CorpActivityChip :source="client" :small="false" />
        <CorpTagChips v-if="isAdmin" :tags="client.tags" :small="false" class="ml-4" />
        <v-switch v-if="!isProfile && $store.getters['auth/isAllowed']('edit_corpclient')"
          :input-value="client.send_mail" @change="changeSendMail" :loading="loading.editMail"
          class="v-input--reverse profile-switch ml-4" hide-details :color="client.send_mail ? 'success' : 'error'"
        >
          <div slot="label" :class="client.send_mail ? 'success--text' : 'error--text'">Почта</div>
        </v-switch>
      </div>
    </ProfileTopPanel>

    <StateBlock v-if="!client && !loading.client && errors.client.length" state="error" @retry="reload" />

    <template v-if="client">
      <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
        <v-tabs :value="currentTab" class="card-tabs" slider-size="3" optional>
          <v-tab v-for="tab in tabs" :key="tab.value" :tab-value="tab.value" @click="openTab(tab.value)">{{ tab.title }}</v-tab>
        </v-tabs>
        <div class="card-actions">
          <template v-if="currentTab === 'overview'">
            <v-btn outlined @click="callTab('exportXlsx')"><v-icon left small>mdi-download</v-icon> Выгрузить</v-btn>
            <v-btn v-if="canAccrue" outlined color="primary" @click="callTab('openTransfer', 'accrue')">Начислить</v-btn>
            <v-btn v-if="canBuy" outlined color="primary" :to="{ name: 'list-services' }">Купить услуги</v-btn>
            <v-btn v-if="canDistribute" color="primary" :disabled="!wallet || !wallet.bufferBalance" @click="callTab('openTransfer', 'distribute')">
              Распределить из буфера
            </v-btn>
          </template>
          <template v-else-if="currentTab === 'managers'">
            <v-btn v-if="canDistribute" outlined color="primary" :disabled="!wallet || !wallet.bufferBalance" @click="callTab('openDistribute', null)">
              Распределить из буфера
            </v-btn>
            <v-btn v-if="canAddManager" color="primary" @click="callTab('openCreate')">+ Добавить менеджера</v-btn>
          </template>
          <v-btn v-else-if="currentTab === 'analytics' || currentTab === 'operations'" outlined @click="callTab('exportXlsx')">
            <v-icon left small>mdi-download</v-icon> Выгрузить в Excel
          </v-btn>
        </div>
      </div>

      <ClientOverview
        v-if="currentTab === 'overview'"
        ref="tabView"
        :corp-id="corpId"
        :can-add-manager="canAddManager"
        :client="client"
        :wallet="wallet"
        :wallet-loading="loading.wallet"
        :wallet-error="errors.wallet"
        :period.sync="period"
        :custom-range.sync="customRange"
        :wallet-period="walletPeriod"
        :is-admin="isAdmin"
        :can-accrue="canAccrue"
        :can-distribute="canDistribute"
        :can-buy="canBuy"
        :partner-name="partnerName"
        :referrer-name="referrerName"
        :show-partner="isAdmin"
        @open-tab="openTab"
        @add-manager="openAddManager"
        @reload="loadWallet"
      />
      <ClientManagers
        v-else-if="currentTab === 'managers'"
        ref="tabView"
        :corp-id="corpId"
        :wallet="wallet"
        :can-add-manager="canAddManager"
        :can-distribute="canDistribute"
        :open-add-on-start="!!$route.query.addManager"
        @reload="loadWallet"
      />
      <ClientAnalytics
        v-else-if="currentTab === 'analytics'"
        ref="tabView"
        :corp-id="corpId"
        :company="client.company"
        :self="isCorpSelf"
      />
      <ClientOperations
        v-else-if="currentTab === 'operations'"
        ref="tabView"
        :corp-id="corpId"
        :company="client.company"
        :wallet="wallet"
        :self="isCorpSelf"
      />
      <OrdersTable
        v-else-if="currentTab === 'orders'"
        :mode="isCorpSelf ? 'my' : 'corp'"
        :email="client.email"
        @changed="loadWallet"
      />
      <ClientCandidates
        v-else-if="currentTab === 'candidates'"
        :corp-id="corpId"
        :company="client.company"
        :managers="wallet ? wallet.managers : []"
      />

      <div v-if="PARTNER_FEATURES && currentTab === 'overview'" class="row mt-4">
        <div class="col-12 col-md-6">
          <ClientProfileCardRef class="mb-5" />
          <ClientProfileCardCodesUsed class="mb-5" />
          <ClientProfileCardCalc class="mb-5" />
        </div>
      </div>
    </template>

    <ClientEditDialog />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import api from '@/common/api';
import ProfileTopPanel from '@/components/common/ProfileTopPanel';
import ClientEditDialog from '@/components/clients/ClientEditDialog';
import ClientProfileCardRef from '@/components/clients/profile/ProfileCardRef';
import ClientProfileCardCodesUsed from '@/components/clients/profile/ProfileCardCodesUsed';
import ClientProfileCardCalc from '@/components/clients/profile/ProfileCardCalc';
import ClientOverview from '@/components/clients/card/ClientOverview.vue';
import ClientManagers from '@/components/clients/card/ClientManagers.vue';
import ClientCandidates from '@/components/clients/card/ClientCandidates.vue';
import StateBlock from '@/components/ui/StateBlock.vue';
import OrdersTable from '@/components/orders/OrdersTable.vue';
import ClientAnalytics from '@/components/clients/card/ClientAnalytics.vue';
import ClientOperations from '@/components/clients/card/ClientOperations.vue';
import CorpActivityChip from '@/components/clients/card/CorpActivityChip.vue';
import CorpTagChips from '@/components/clients/tags/CorpTagChips.vue';
import {
  CUSTOM_PERIOD, DEFAULT_WALLET_PERIOD, defaultCustomRange, walletPeriodRange,
} from '@/components/clients/card/walletPeriods';
import { PARTNER_FEATURES } from '@/common/constants/features';
import { partnerIdOf, partnerFullName } from '@/common/models/partnerTypes';
import { ADMIN, CORPCLIENT } from '@/common/constants/userTypes';

const TABS = [
  { value: 'overview', title: 'Обзор' },
  { value: 'managers', title: 'Менеджеры', permission: ['bookmark_managers', 'add_manager'] },
  { value: 'candidates', title: 'Кандидаты', permission: 'bookmark_candidates' },
  { value: 'analytics', title: 'Аналитика', permission: 'bookmark_analytics' },
  { value: 'operations', title: 'История операций', permission: 'bookmark_analytics' },
  // Заказы: админ видит их у корпа, сам корп — свои
  { value: 'orders', title: 'Заказы', adminOrSelf: true },
];

// Карточка корпа — один компонент для двух зрителей: админ открывает её из списка (/clients/:id/:tab),
// корп видит её как свой «Личный кабинет» (/profile?tab=). Отличия — только в правах (ТЗ, 3.1).
export default {
  name: 'ProfileClient',
  components: {
    ClientEditDialog,
    ClientProfileCardRef,
    ClientProfileCardCodesUsed,
    ClientProfileCardCalc,
    ProfileTopPanel,
    ClientOverview,
    ClientManagers,
    ClientCandidates,
    StateBlock,
    OrdersTable,
    ClientAnalytics,
    ClientOperations,
    CorpActivityChip,
    CorpTagChips,
  },
  props: ['userId'],
  provide() {
    return {
      userType: 'CorpClient',
      userId: this.userId,
    };
  },
  inject: {
    isProfile: {
      default: false
    }
  },
  data() {
    return {
      PARTNER_FEATURES,
      period: DEFAULT_WALLET_PERIOD,
      customRange: defaultCustomRange(),
      partners: [],
    };
  },
  computed: {
    ...mapGetters({
      client: 'users/clients/client',
      loading: 'users/clients/loading',
      errors: 'users/clients/errors',
      wallet: 'users/clients/wallet',
      walletPeriod: 'users/clients/walletPeriod',
      user: 'auth/user',
    }),
    corpId() {
      return this.userId;
    },
    tabs() {
      // Корп видит вкладки по тем же правам, что раньше открывали пункты меню; админ в карточке — все
      // Заказы видят только админ (в карточке) и сам корп
      const base = TABS.filter((t) => !t.adminOrSelf || this.isAdmin || this.isCorpSelf);
      if (!this.isCorpSelf) return base;
      return base.filter((t) => !t.permission || this.allowed([].concat(t.permission)));
    },
    currentTab() {
      const tab = this.isProfile ? this.$route.query.tab : this.$route.params.tab;
      return this.tabs.some((t) => t.value === tab) ? tab : 'overview';
    },
    isAdmin() {
      return !!this.user && this.user.user_type === ADMIN;
    },
    isCorpSelf() {
      return this.isProfile && !!this.user && this.user.user_type === CORPCLIENT;
    },
    allowed() {
      return this.$store.getters['auth/isAllowed'];
    },
    canAccrue() {
      return !this.isProfile && !!this.$store.getters['users/counters/getEndpoint']('corp_client')
        && this.allowed(['add_stockamount_corpclient', 'add_stockamount']);
    },
    // Распределяет из буфера только сам корп: у админа перевод менеджеру шёл бы с его собственного счёта (В-2).
    canDistribute() {
      return this.isCorpSelf && this.allowed(['add_stockamount_manager', 'add_stockamount']);
    },
    canAddManager() {
      return this.isCorpSelf && this.allowed('add_manager');
    },
    canBuy() {
      if (!this.isCorpSelf) return false;
      const { route } = this.$router.resolve({ name: 'list-services' });
      return !route.meta.allowed || route.meta.allowed.includes(this.user.user_type);
    },
    contactName() {
      if (!this.client) return '';
      return [this.client.last_name, this.client.first_name].filter(Boolean).join(' ');
    },
    partnerName() {
      return this.linkedPartnerName(this.client && this.client.supervisor);
    },
    referrerName() {
      return this.linkedPartnerName(this.client && this.client.referrer);
    },
    breadcrumbs() {
      if (!this.client) return [];
      if (this.isCorpSelf) return [];
      if (this.isProfile) return [{ title: 'Личный кабинет', url: '/' }];
      const tab = TABS.find((t) => t.value === this.currentTab);
      return [
        { title: 'Клиенты', url: '/clients/' },
        { title: this.client.company || this.contactName || 'Корп. клиент', url: `/clients/${this.client.id}` },
        ...(tab ? [{ title: tab.title, url: this.$route.fullPath }] : []),
      ];
    },
  },
  watch: {
    $route() {
      this.redirectLegacyTab();
    },
    period() {
      this.loadWallet();
    },
    customRange() {
      if (this.period === CUSTOM_PERIOD) this.loadWallet();
    },
    userId() {
      this.reload();
    },
  },
  methods: {
    linkedPartnerName(link) {
      const id = partnerIdOf(link);
      if (!id) return '';
      const partner = typeof link === 'object' ? link : this.partners.find((p) => String(p.id) === String(id));
      return partner ? partnerFullName(partner) : '';
    },
    ...mapActions({
      loadClient: 'users/clients/loadClient',
      loadClientWallet: 'users/clients/loadClientWallet',
      setDialogEditShow: 'users/clients/setDialogEditShow',
      setClientSendMail: 'users/clients/setClientSendMail',
    }),
    ...mapMutations({
      setEditedClient: 'users/clients/setEditedClient',
    }),
    // Вкладка «Остатки» объединена с «Обзором»: старые ссылки ведут на обзор
    redirectLegacyTab() {
      const rawTab = this.isProfile ? this.$route.query.tab : this.$route.params.tab;
      if (rawTab !== 'balances') return;
      if (this.isProfile) {
        const { tab, ...query } = this.$route.query;
        this.$router.replace({ query });
      } else {
        this.$router.replace({ name: 'clients-id', params: { id: this.corpId } });
      }
    },
    openTab(tab) {
      if (tab === this.currentTab) return;
      if (this.isProfile) {
        this.$router.push({ query: tab === 'overview' ? {} : { tab } });
      } else {
        this.$router.push({ name: 'clients-id', params: { id: this.corpId, tab: tab === 'overview' ? undefined : tab } });
      }
    },
    // Кнопки в шапке вызывают действие открытой вкладки (диалоги и выгрузка живут в ней)
    callTab(method, ...args) {
      const view = this.$refs.tabView;
      if (view && typeof view[method] === 'function') view[method](...args);
    },
    openAddManager() {
      if (this.isProfile) this.$router.push({ query: { tab: 'managers', addManager: 1 } });
      else this.$router.push({ name: 'clients-id', params: { id: this.corpId, tab: 'managers' }, query: { addManager: 1 } });
    },
    loadWallet() {
      const { from, to } = walletPeriodRange(this.period, this.customRange);
      this.loadClientWallet({
        id: this.corpId, from, to, self: this.isCorpSelf,
      });
    },
    onEdit() {
      this.setEditedClient({ ...this.client });
      this.setDialogEditShow(true);
    },
    onDelete() {
    },
    reload() {
      this.loadClient(this.userId).catch(() => {});
      this.loadWallet();
    },
    async loadPartners() {
      try {
        const response = await api.get('/users/partners/', { params: { page_size: -1 } });
        this.partners = Array.isArray(response) ? response : (response.results || []);
      } catch (e) {
        this.partners = [];
      }
    },
    changeSendMail(value) {
      this.setClientSendMail({ send_mail: !!value, id: this.client.id });
    },
  },
  created() {
    this.redirectLegacyTab();
    // Пришли из шага «Добавить менеджера?» — сразу на вкладку «Менеджеры»
    if (this.$route.query.addManager && this.currentTab !== 'managers') {
      if (this.isProfile) this.$router.replace({ query: { tab: 'managers', addManager: 1 } });
      else this.$router.replace({ name: 'clients-id', params: { id: this.corpId, tab: 'managers' }, query: { addManager: 1 } });
    }
    this.reload();
    if (this.isAdmin) this.loadPartners();
  },
};
</script>

<style scoped lang="scss">
@import "src/assets/styles/variables";

.card-tabs {
  flex: 1 1 auto;
  width: auto;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// Вкладки карточки как в прототипе: обычный регистр, полужирные
::v-deep .v-tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  font-size: 15px;
}
</style>
